// @material-ui
import { Chip, Grid, Slider, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
// other
import { isArray } from 'lodash';
import React from 'react';

// Publica imports
import { IParam } from '~/src/dash/api/network';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { isPublica } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  EQueryParamCategoryElementType,
  IQueryParam,
  IQueryParamCategory,
  IQueryParamGroup,
} from '~/src/dash/types/endpoint-query-params';
import { IMenuList } from '~/src/dash/types/menuList';
import { IBaseState } from '~/src/dash/types/props';
import {
  ALL,
  queryParamToIMenuList,
  selectedValueMapToString,
  setParamValues,
  updateSelectedParams,
} from '~/src/dash/utils/endpoint-builder-helpers';

interface IEndpointBuilderProps {
  paramGroups: IQueryParamGroup[];

  // active params
  endpointParamsList: IParam[];

  onEndpointParamsListChange: (endpointURLParams: string) => void;
  displayDependencies: { [k: string]: string[] };
  selectDependencies: { [k: string]: IQueryParam[] };
  multiSelectDependencies: { [k: string]: string[] };
}

interface IEndpointBuilderState extends IBaseState {
  // params that have changeable values (i.e. in the slider) should be added here
  paramsObject: { [k: string]: string };
}

export interface ISelectedValuesMap {
  [key: string]: string;
}

const DEFAULT_ACTIVE_TEXT_COLOR = isPublica() ? '#60c7bc' : '#ad0509';
const DEFAULT_ACTIVE_BACKGROUND_COLOR = isPublica() ? '#edfffd' : '#fcebeb';

const SLIDER_COLOR = isPublica() ? '#09ae82' : '#ae1c09';

export class EndpointBuilderV2 extends React.Component<
  IEndpointBuilderProps,
  IEndpointBuilderState
> {
  state = {
    // add the paramName for any params with changeable values (i.e. in the slider) to the paramNames list and add the default value to the state
    paramsObject: {
      min_ad_duration: '6',
      max_ad_duration: '120',
    },
  };

  // HANDLER FUNCTIONS //
  // DO NOT ADD DEPENDENCIES (SPECIFIC IF STATEMENTS, ETC) IN THESE FUNCTIONS, SHOULD BE SPECIFIED IN NETWORK OR CHANNEL QUERY PARAMS //

  // handles changing the values stored in state for params with changeable values
  handleChangeParamValue = (params: { [k: string]: string }) => {
    const paramsObject: { [k: string]: string } = Object.assign({}, this.state.paramsObject);
    for (const k in params) {
      if (paramsObject.hasOwnProperty(k)) {
        paramsObject[k] = params[k];
      }
    }
    this.setState({
      paramsObject,
    });
  };

  // select any params that should be automatically selected along with another param,
  // that relationship should be defined in the `selectDependencies` object
  selectDependentParams = (newParams: IMenuList, param: IQueryParam) => {
    const autoSelectedValues: ISelectedValuesMap = {};
    const excludedParams: IQueryParam[] = [];
    const selectDependenciesCopy: { [k: string]: IQueryParam[] } = Object.assign(
      {},
      this.props.selectDependencies,
    );
    selectDependenciesCopy[param.paramName].forEach((paramToSelect, idx) => {
      let paramValue: string = '';

      // if the param has a changeable value, use the stored value rather than the default
      if (this.state.paramsObject.hasOwnProperty(paramToSelect.paramName)) {
        paramValue = setParamValues(
          param,
          paramToSelect,
          idx,
          this.state.paramsObject,
          selectDependenciesCopy,
        );
      } else {
        paramValue = paramToSelect.paramValue;
      }
      autoSelectedValues[paramToSelect.paramName] = paramValue;

      // if there are any values that should not be selected along with this value, we will set them to deselected by adding them to the first, but not second arg passed to queryParamToIMenuList
      // (by definition of a dependency, this exclusion should not be included in selectDependencies[param.paramName])
      if (paramToSelect.exclusions) {
        paramToSelect.exclusions.forEach((exclusion) => {
          excludedParams.push({
            text: '',
            paramName: exclusion,
            paramValue: '',
          });
        });
      }
    });
    Object.assign(
      newParams,
      queryParamToIMenuList(
        selectDependenciesCopy[param.paramName].concat(excludedParams),
        autoSelectedValues,
      ),
    );
  };

  // remove the selection of any params that are selected as a dependency of another selected param
  deselectDependentParams = (
    newParams: IMenuList,
    param: IQueryParam,
    selectedParamNames: ISelectedValuesMap,
  ) => {
    const paramsToKeepSelected: ISelectedValuesMap = {};
    const paramsToDeselect: string[] = [param.paramName];
    const selectDependenciesCopy: { [k: string]: IQueryParam[] } = Object.assign(
      {},
      this.props.selectDependencies,
    );
    selectDependenciesCopy[param.paramName].forEach((dependentParam, idx) => {
      // if the param is dependent on more than one param
      if (this.props.multiSelectDependencies[dependentParam.paramName]) {
        this.props.multiSelectDependencies[dependentParam.paramName].forEach((parentParamName) => {
          // if the parent Param is or is being selected, don't deselect the dependent
          const paramSelectedOrBeingSelected =
            selectedParamNames[parentParamName] ||
            (newParams[parentParamName] && newParams[parentParamName].selected);
          if (!paramsToDeselect.includes(parentParamName) && paramSelectedOrBeingSelected) {
            let paramValue: string = '';
            if (this.state.paramsObject.hasOwnProperty(dependentParam.paramName)) {
              paramValue = setParamValues(
                param,
                dependentParam,
                idx,
                this.state.paramsObject,
                selectDependenciesCopy,
              );
            } else {
              paramValue = dependentParam.paramValue;
            }
            paramsToKeepSelected[dependentParam.paramName] = paramValue;
          }
        });

        // if we didn't specify to keep the param, add it to the tracking list of params to be deselected
        // important in case there are other params in the list dependent on this param
        if (!paramsToKeepSelected[dependentParam.paramName]) {
          paramsToDeselect.push(dependentParam.paramName);
        }
      } else {
        // the dependentParam is only dependent on the single param
        paramsToDeselect.push(dependentParam.paramName);
      }
    });

    // newParams to track removal of all dependent params not specified to be kept selected
    Object.assign(
      newParams,
      queryParamToIMenuList(selectDependenciesCopy[param.paramName], paramsToKeepSelected),
    );
  };

  handleChangeChipSelection = (
    paramCategory: IQueryParamCategory,
    param: IQueryParam,
    selectedParamNames: ISelectedValuesMap,
  ) => {
    if (paramCategory.multiSelect) {
      const newParams = {
        // Create as IListItem
        [param.paramName]: {
          ID: param.paramName,
          selected: !selectedParamNames[param.paramName],
          title: param.text,
          description: param.paramValue, // Used to hold the macro value
        },
      };
      if (this.props.selectDependencies[param.paramName]) {
        if (newParams[param.paramName].selected) {
          this.selectDependentParams(newParams, param);
        } else {
          this.deselectDependentParams(newParams, param, selectedParamNames);
        }
      }

      updateSelectedParams(newParams, selectedParamNames);
    } else {
      const newParams: IMenuList = {};
      if (paramCategory.mandatory || !selectedParamNames[param.paramName]) {
        // select the param, deselect all other params in this category since multiselect = false
        const selectedValues = {
          [param.paramName]: param.paramValue,
        };
        Object.assign(newParams, queryParamToIMenuList(paramCategory.queryParams, selectedValues));

        // should anything be selected along with this param? Do any other things need to be deselected
        paramCategory.queryParams.forEach((queryParam) => {
          // select any dependencies
          if (queryParam === param && this.props.selectDependencies[queryParam.paramName]) {
            this.selectDependentParams(newParams, queryParam);
          } else if (this.props.selectDependencies[queryParam.paramName]) {
            this.deselectDependentParams(newParams, queryParam, selectedParamNames);
          }
        });
      } else {
        // deselect the param, check if dependencies should also be deselected
        Object.assign(newParams, queryParamToIMenuList(paramCategory.queryParams, {}));

        if (this.props.selectDependencies[param.paramName]) {
          this.deselectDependentParams(newParams, param, selectedParamNames);
        }
      }

      updateSelectedParams(newParams, selectedParamNames);
    }

    this.props.onEndpointParamsListChange(selectedValueMapToString(selectedParamNames));
  };

  handleChangeDropdownSelection = (
    newParams: IMenuList,
    selectedParamNames: ISelectedValuesMap,
  ) => {
    updateSelectedParams(newParams, selectedParamNames);
    this.props.onEndpointParamsListChange(selectedValueMapToString(selectedParamNames));
  };

  handleChangeSliderSelection = (
    _: React.ChangeEvent<{}>,
    newValues: number | number[],
    paramCategory: IQueryParamCategory,
    selectedParamNames: ISelectedValuesMap,
  ) => {
    if (isArray(newValues)) {
      const selectedParams: ISelectedValuesMap = {};
      paramCategory.queryParams.forEach((queryParam, idx) => {
        queryParam.paramValue = `${newValues[idx]}`;
        selectedParams[queryParam.paramName] = `${newValues[idx]}`;
      });
      this.handleChangeParamValue(selectedParams);
      updateSelectedParams(
        queryParamToIMenuList(paramCategory.queryParams, selectedParams),
        selectedParamNames,
      );
      this.props.onEndpointParamsListChange(selectedValueMapToString(selectedParamNames));
    }
  };

  // RENDER FUNCTIONS //

  // use displayDependencies to set display to true or false for dependent param categories
  determineCategoryDisplays = (
    selectedValuesMap: ISelectedValuesMap,
    paramCategories: IQueryParamCategory[],
  ) => {
    paramCategories.forEach((category) => {
      if (this.props.displayDependencies[category.title]) {
        if (this.props.displayDependencies[category.title][0] === ALL) {
          if (
            this.props.displayDependencies[category.title]
              .slice(1)
              .every((ele) => selectedValuesMap[ele])
          ) {
            category.display = true;
          } else {
            category.display = false;
          }
        } else {
          if (
            this.props.displayDependencies[category.title]
              .slice(1)
              .some((ele) => selectedValuesMap[ele])
          ) {
            category.display = true;
          } else {
            category.display = false;
          }
        }
      }
    });
  };

  renderParamGroups = (paramGroup: IQueryParamGroup) => {
    const selectedParamNames: ISelectedValuesMap = this.props.endpointParamsList.reduce(
      (partialMap, param) => ({ ...partialMap, [param.name]: param.value }),
      {},
    );

    // determine whether certain paramCategories should be displayed based on the selected params
    this.determineCategoryDisplays(selectedParamNames, paramGroup.queryParamCategories);

    return paramGroup.queryParamCategories.map((category) => {
      if (category.display) {
        return this.renderParamCategory(category, selectedParamNames);
      } else {
        return;
      }
    });
  };

  renderParamCategory = (
    paramCategory: IQueryParamCategory,
    selectedParamNames: ISelectedValuesMap,
  ) => {
    return (
      <Grid
        container
        direction='row'
        justify='flex-start'
        alignItems='flex-start'
        style={{
          display: 'grid',
          gridTemplateColumns: '30% 70%',
          alignItems: 'center',
          marginTop: '10px',
          marginLeft: '12px',
          marginBottom: '10px',
        }}
      >
        <Grid container direction='column'>
          <Grid
            container
            direction='row'
            style={{
              marginTop: '0px',
              marginBottom: '0px',
            }}
          >
            <Grid item>
              <Typography
                style={{
                  fontSize: '1rem',
                }}
              >
                {paramCategory.title}
              </Typography>
            </Grid>

            {paramCategory.description && (
              <Grid item>
                <Tooltip title={paramCategory.description}>
                  <InfoIcon
                    style={{
                      verticalAlign: 'center',
                      width: '1rem',
                      color: '#dedede',
                      marginLeft: '5px',
                    }}
                  />
                </Tooltip>
              </Grid>
            )}
          </Grid>

          {paramCategory.subTitle && (
            <Typography
              style={{
                fontSize: '10px',
                color: 'rgb(117, 117, 117)',
                marginTop: '0px',
                marginBottom: '0px',
              }}
            >
              {paramCategory.subTitle}
            </Typography>
          )}
        </Grid>

        {paramCategory.elementType === EQueryParamCategoryElementType.CHIPS &&
          this.renderChip(paramCategory, selectedParamNames)}
        {paramCategory.elementType === EQueryParamCategoryElementType.DROPDOWN &&
          this.renderDropdown(paramCategory, selectedParamNames)}
        {paramCategory.elementType === EQueryParamCategoryElementType.SLIDER &&
          this.renderSlider(paramCategory, selectedParamNames)}
      </Grid>
    );
  };

  renderChip = (paramCategory: IQueryParamCategory, selectedParamNames: ISelectedValuesMap) => (
    <Grid container direction='row' justify='flex-start' alignItems='flex-start'>
      {paramCategory.queryParams.map((param) => {
        return (
          param.text !== '' && (
            <Grid item>
              <Chip
                label={param.text}
                style={{
                  marginBottom: 'unset',
                  marginTop: 'unset',
                  marginRight: '10px',
                  color: selectedParamNames[param.paramName]
                    ? DEFAULT_ACTIVE_TEXT_COLOR
                    : 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: selectedParamNames[param.paramName]
                    ? DEFAULT_ACTIVE_BACKGROUND_COLOR
                    : '#e0e0e0',
                }}
                onClick={() =>
                  this.handleChangeChipSelection(paramCategory, param, selectedParamNames)
                }
              />
            </Grid>
          )
        );
      })}
    </Grid>
  );

  renderDropdown = (paramCategory: IQueryParamCategory, selectedParamNames: ISelectedValuesMap) => (
    <MaterialSearchDropdownMulti
      menuTitle='params'
      selectAll={paramCategory.multiSelect}
      list={queryParamToIMenuList(paramCategory.queryParams, selectedParamNames)}
      onChange={(newParams) => this.handleChangeDropdownSelection(newParams, selectedParamNames)}
      width='70%'
    />
  );

  renderSlider = (paramCategory: IQueryParamCategory, selectedParamNames: ISelectedValuesMap) => (
    <Slider
      defaultValue={[
        parseInt(selectedParamNames.min_ad_duration, 10) || 6,
        parseInt(selectedParamNames.max_ad_duration, 10) || 120,
      ]}
      min={6}
      max={120}
      marks={[
        {
          value: 6,
          label: '6',
        },
        {
          value: 15,
          label: '15',
        },
        {
          value: 30,
          label: '30',
        },
        {
          value: 45,
          label: '45',
        },
        {
          value: 60,
          label: '60',
        },
        {
          value: 90,
          label: '90',
        },
        {
          value: 120,
          label: '120',
        },
      ]}
      step={null}
      onChange={(_, newValues) =>
        this.handleChangeSliderSelection(_, newValues, paramCategory, selectedParamNames)
      }
      style={{
        width: '70%',
        color: SLIDER_COLOR,
      }}
    ></Slider>
  );

  render = () => (
    <Grid
      container
      direction='column'
      justify='flex-start'
      alignItems='flex-start'
      style={{
        padding: 8,
      }}
    >
      {this.props.paramGroups.map((paramGroup, index) => (
        <Grid
          item
          style={{
            width: '100%',
          }}
        >
          <Typography
            style={{
              fontWeight: 'bold',
              fontSize: '1rem',
              marginTop: index !== 0 ? '20px' : '0px',
            }}
          >
            •&nbsp;&nbsp;{paramGroup.name}
            {paramGroup.subtitle && (
              <Typography
                style={{
                  fontSize: 13,
                  width: '80%',
                  marginLeft: '10px',
                }}
              >
                {paramGroup.subtitle}
              </Typography>
            )}
          </Typography>
          {this.renderParamGroups(paramGroup)}
        </Grid>
      ))}
    </Grid>
  );
}
